import * as React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import cx from 'classnames';

import { CustomMenu } from './components/custom-menu';

import './ActionsButton.styles.scss';

export const TRIGGER_TYPE = {
    hover: 'hover',
    click: 'click',
};

const MENU_ITEM_HORIZONTAL_PADDING = 8;

export default class ActionsButton extends React.Component {
    static defaultProps = {
        setCurrent: () => {},
        menuItems: [],
        triggerType: TRIGGER_TYPE.click,
        withStopPropagation: true,
        withBackdrop: true,
        classes: {},
    }

    ref = React.createRef();
    state = {
        isOpen: false,
        menuPlacement: 'auto',
    };

    getMenuPlacement() {
        const bodyRect = document.body.getBoundingClientRect();
        const elemRect = this.ref.current.getBoundingClientRect();
        const offset = elemRect.top - bodyRect.top;

        if (offset > (bodyRect.height / 2)) {
            return 'top';
        } 

        return 'bottom';
    }

    componentDidMount() {
        this.setState((x) => ({
            ...x,
            menuPlacement: this.getMenuPlacement()
        }));
    }

    get menuItems() {
        const { menuItems, id, data } = this.props;

        return menuItems.map(({ onClick = () => {}, label, disabled }, i) => ({
            disabled,
            label,
            value: label,
            onClick: () => {
                this.close();
                onClick(id, data);
            },
        }));
    }

    open = (e) => {
        e.persist();

        const { withStopPropagation, id, data, onOpen } = this.props;

        if (withStopPropagation) {
            e.stopPropagation();
        }

        this.setState(
            (state) => ({
                ...state,
                isOpen: !state.isOpen
            }),
            () => {
                if (this.state.isOpen) {
                    const { setCurrent, id } = this.props;

                    setCurrent(id, this.props.data);
                }
            }
        );

        if (onOpen) {
            onOpen(id, data);
        }
    }

    close = () => {
        this.setState((state) => ({
            ...state,
            isOpen: false
        }));
    }

    get menuWidth() {
        const { menuItems } = this.props;

        const longestWord = menuItems
            .map(({ label }) => label)
            .reduce((acc, currentTitle) => acc.length < currentTitle.length ? currentTitle : acc, '');
        const pxPerChar = 7.5;

        return longestWord.length * pxPerChar + MENU_ITEM_HORIZONTAL_PADDING * 2;
    }

    getStyles = (baseStyles) => {
        return {
            ...baseStyles, 
            zIndex: 9999999,
            width: this.menuWidth 
        };
    }

    handleSelect = ({ onClick }) => {
        onClick();
    }

    renderTriggerBtn = () => {
        const { variant = 'lines', classes = {}, id, trigger, getId, openOnClick } = this.props;
        const elementId = getId?.triggerBtn ? getId?.triggerBtn() : 'dropdown-basic-' + id;

        if (trigger) {
            return (
                <span 
                    id={elementId}
                    role="button"
                    onClick={openOnClick ? this.open : undefined}
                >
                    {trigger}
                </span>
            );
        }

        const variantView = {
            lines: (
                <button
                    id={elementId}
                    className={cx('ActionsButton__button ActionsButton__button-lines', classes.triggerBtn)}
                    onClick={this.open}
                >
                    <i className="fa fa-bars" />
                    <i className="fa fa-caret-down" />
                </button>
            ),
            'arrow-down': (
                <button 
                    id={elementId}
                    className={cx('ActionsButton__button ActionsButton__button-arrow-down', classes.triggerBtn)}
                    onClick={this.open}
                >
                    <span className="fa fa-angle-down ActionsButton__button-arrow-down-icon" />
                </button>
            )
        };  

        return variantView[variant];
    }

    get customMenuPositioning() {
        const { current } = this.ref;

        if (current) {
            const { top, left, width } = current.getBoundingClientRect();

            return {
                top,
                left: left + width,
            };
        }

        return {};
    }

    get customMenu() {
        if (!this.state.isOpen) {
            return null;
        }

        const { children, classes, triggerType, useDefaultPosition } = this.props;
        const shouldTriggerOnHover = triggerType === TRIGGER_TYPE.hover;

        return (
          <CustomMenu
            className={cx(
              "ActionsButton__custom-menu-container",
              classes.menuContainer
            )}
            useDefaultPosition={useDefaultPosition}
            customMenuPositioning={this.customMenuPositioning}
            onMouseLeave={shouldTriggerOnHover ? this.close : undefined}
          >
            {children}
          </CustomMenu>
        );
    }

    handleBackdropClick = (e) => {
        e.stopPropagation();
        this.close();
    }

    stopPropagation = (e) => {
        e.stopPropagation();
    }

    isOptionDisabled = ({ disabled }) => disabled;

    render() {
        const { isOpen, menuPlacement } = this.state;
        const { withBackdrop, triggerType, children, className, withStopPropagationOnBackdrop, stopPropagationOnRoot, disabled } = this.props;
        const shouldTriggerOnHover = triggerType === TRIGGER_TYPE.hover;

        return (
            <span 
                ref={this.ref}
                style={{ pointerEvents: disabled ? 'none' : 'auto' }}
                className={cx('ActionsButton', className)} 
                onMouseEnter={shouldTriggerOnHover ? this.open : undefined} 
                onMouseLeave={shouldTriggerOnHover ? this.close : undefined}
                onClick={stopPropagationOnRoot ? this.stopPropagation : undefined}
            >
                {this.renderTriggerBtn()}
                {withBackdrop && isOpen && (
                    <div 
                        className="ActionsButton__backdrop" 
                        onContextMenu={withStopPropagationOnBackdrop ? this.stopPropagation : undefined} 
                        onClick={this.handleBackdropClick} 
                    />
                )}
                <div className="ActionsButton__menu__wrapper">
                    {children 
                        ? this.customMenu
                        : (
                            <Select
                                closeMenuOnSelect
                                styles={{
                                    menuPortal: this.getStyles
                                }}
                                menuIsOpen={isOpen}
                                menuPortalTarget={document.body}
                                isSearchable={false}
                                value={null}
                                isOptionDisabled={this.isOptionDisabled}
                                options={this.menuItems}
                                menuPosition="fixed"
                                className="ActionsButton__menu-container"
                                classNamePrefix="ActionsButton__menu"
                                menuShouldScrollIntoView={false}
                                menuPlacement={menuPlacement}
                                onChange={this.handleSelect}
                            />
                        )
                    }
                </div>
            </span>
        );
    }
}

ActionsButton.propTypes = {
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    menuItems: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]).isRequired,
            onClick: PropTypes.func,
        })
    ),
    setCurrent: PropTypes.func.isRequired,
};